/**
 * Structural comparison and sharing for UI game state. UI state is plain
 * data (primitives, arrays and plain objects), and it crosses the worker
 * boundary by structured clone, so every object in a patch arrives as a new
 * reference even when nothing in it changed. These helpers let the game skip
 * publishing values that didn't change, and let the page keep the references
 * of the parts that didn't, so `Object.is` selectors stay quiet.
 *
 * Kept free of React so the game worker can use them too.
 */

function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (value === null || typeof value !== 'object') {
        return false;
    }
    const prototype: unknown = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

/** Whether two pieces of plain data hold the same values, all the way down. */
export function isStructurallyEqual(left: unknown, right: unknown): boolean {
    if (Object.is(left, right)) {
        return true;
    }

    if (Array.isArray(left)) {
        if (!Array.isArray(right) || left.length !== right.length) {
            return false;
        }
        for (let index = 0; index < left.length; index += 1) {
            if (!isStructurallyEqual(left[index], right[index])) {
                return false;
            }
        }
        return true;
    }

    if (isPlainObject(left)) {
        if (!isPlainObject(right)) {
            return false;
        }
        const leftKeys = Object.keys(left);
        if (leftKeys.length !== Object.keys(right).length) {
            return false;
        }
        for (const key of leftKeys) {
            if (!Object.hasOwn(right, key) || !isStructurallyEqual(left[key], right[key])) {
                return false;
            }
        }
        return true;
    }

    // Anything else (class instances, functions) is only equal to itself.
    return false;
}

/**
 * Returns `next`, but reusing every part of `previous` that is structurally
 * equal to the matching part of `next`: `previous` itself when nothing
 * changed. A changed object or array is a new one whose unchanged children
 * keep their old references.
 */
export function replaceEqualDeep<T>(previous: T, next: T): T {
    if (Object.is(previous, next)) {
        return previous;
    }

    if (Array.isArray(previous) && Array.isArray(next)) {
        let isSame = previous.length === next.length;
        const shared: unknown[] = new Array<unknown>(next.length);
        for (let index = 0; index < next.length; index += 1) {
            const item: unknown = replaceEqualDeep<unknown>(previous[index], next[index]);
            shared[index] = item;
            if (item !== previous[index]) {
                isSame = false;
            }
        }
        return (isSame ? previous : shared) as T;
    }

    if (isPlainObject(previous) && isPlainObject(next)) {
        const nextKeys = Object.keys(next);
        let isSame = nextKeys.length === Object.keys(previous).length;
        const shared: Record<string, unknown> = {};
        for (const key of nextKeys) {
            const hadKey = Object.hasOwn(previous, key);
            const value: unknown = hadKey ? replaceEqualDeep<unknown>(previous[key], next[key]) : next[key];
            shared[key] = value;
            if (!hadKey || value !== previous[key]) {
                isSame = false;
            }
        }
        return (isSame ? previous : shared) as T;
    }

    return next;
}
